import { Fragment, useEffect, useState } from "react";
import PageTitle from "../../layouts/PageTitle";
import axios from "axios";
import ProductsHistory from "./ProductsHistory";
import config from "../../../../config.json";
import swal from "sweetalert";
import "../../../i18n";
import { useTranslation } from "react-i18next";
import imgVide from "../../../assets/images/vide.png";

const ProductsGridHistory = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");
  const [isError, setIsError] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${config.dev}/v2/products/retrieve`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`, // Récupérer le token
        },
      });
      const data = response?.data || [];
      // console.log("Fetched data:", data); // Log des données récupérées
      setProducts(data);
      setFilteredProducts(data);
      setIsError(false); // Réinitialise l'état d'erreur
    } catch (err) {
      // Définit un message d'erreur clair
      setError("Failed to fetch products. Please try again later.");
      setIsError(true); // Définit l'état d'erreur à vrai
      console.error("Error fetching products:", err);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const results = products.filter((product) => {
      // Vérification du prix
      const matchesPrice = product.price
        ? product.price.toString().includes(searchTerm)
        : false;

      // Vérification des détails du produit
      const matchesDetails = product.productDetails
        ? product.productDetails.some((detail) =>
            ["productTitle", "productDescription", "productHistory"].some(
              (key) =>
                detail[key]?.toLowerCase().includes(searchTerm.toLowerCase())
            )
          )
        : false;

      return matchesPrice || matchesDetails;
    });

    setFilteredProducts(results);
  }, [searchTerm, products]);

  const archivedProducts = filteredProducts.filter((item) => item?.isArchived);
  const selectedCount = archivedProducts.filter((item) =>
    selectedIds.includes(item._id)
  ).length;
  const areAllSelected =
    archivedProducts.length > 0 &&
    archivedProducts.every((item) => selectedIds.includes(item._id));

  useEffect(() => {
    const validIds = products.map((item) => item?._id).filter(Boolean);
    setSelectedIds((prev) => prev.filter((id) => validIds.includes(id)));
  }, [products]);

  const toggleSelectOne = (id, checked) => {
    setSelectedIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      const ids = archivedProducts.map((item) => item._id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
      return;
    }
    const ids = new Set(archivedProducts.map((item) => item._id));
    setSelectedIds((prev) => prev.filter((id) => !ids.has(id)));
  };

  const restoreById = (id) => axios.put(`${config.dev}/v2/products/restore/${id}`);
  const deleteById = (id) => axios.delete(`${config.dev}/v2/products/delete/${id}`);

  const handleRestoreSelected = async () => {
    const ids = archivedProducts.filter((item) => selectedIds.includes(item._id)).map((item) => item._id);
    if (ids.length === 0) return;
    const ok = await swal({ title: "Are you sure?", text: `Restore ${ids.length} selected products?`, icon: "warning", buttons: true });
    if (!ok) return;
    await Promise.all(ids.map(restoreById));
    setSelectedIds([]);
    fetchProducts();
  };

  const handleDeleteSelected = async () => {
    const ids = archivedProducts.filter((item) => selectedIds.includes(item._id)).map((item) => item._id);
    if (ids.length === 0) return;
    const ok = await swal({ title: "Are you sure?", text: `Delete ${ids.length} selected products forever?`, icon: "warning", buttons: true, dangerMode: true });
    if (!ok) return;
    await Promise.all(ids.map(deleteById));
    setSelectedIds([]);
    fetchProducts();
  };

  return (
    <Fragment>
      <PageTitle activeMenu={t("products")} motherMenu={t("archived")} />
      <div className="row">
        <div className="col-lg-6 col-md-12 col-sm-12">
          <input
            type="text"
            className="form-control input-rounded"
            placeholder={t("searchByTitle")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="form-check m-0">
          <input
            type="checkbox"
            id="select-all-products"
            className="form-check-input"
            checked={areAllSelected}
            onChange={(e) => toggleSelectAll(e.target.checked)}
            disabled={archivedProducts.length === 0}
          />
          <label className="form-check-label ms-2" htmlFor="select-all-products">
            Select all ({archivedProducts.length})
          </label>
        </div>
        <div>
          <button className="btn btn-success btn-sm me-2" onClick={handleRestoreSelected} disabled={selectedCount === 0}>
            Restore Selected ({selectedCount})
          </button>
          <button className="btn btn-danger btn-sm" onClick={handleDeleteSelected} disabled={selectedCount === 0}>
            Delete Selected ({selectedCount})
          </button>
        </div>
      </div>
      <div className="row mt-5">
        {filteredProducts.length === 0 ? (
          <div className="col-12">
            {/* Message d'erreur affiché dans une alerte stylisée */}
            <div className="alert alert-warning" role="alert">
              {t("noDataFound")} <i className="fa fa-spinner" />
            </div>
          </div>
        ) : (
          // Affiche les produits sous forme de grille
          filteredProducts.map((product) => (
            <ProductsHistory
              fetchProducts={fetchProducts}
              key={product._id}
              product={product}
              selected={selectedIds.includes(product._id)}
              onToggleSelect={toggleSelectOne}
            />
          ))
        )}
      </div>
    </Fragment>
  );
};

export default ProductsGridHistory;
